import { defineSketchObjectType, registerSketchObjectType } from '../registry';
import { SketchObjectAnchorKind, SketchObjectCategory, type SketchObjectFrame, type SketchObjectResizeHandleLayout } from '../types';
import { at, pushPolyline, pushSegment } from '../glyphs/frame';
import type { ParamValues } from '../paramSchema';
import { MIN_FLIGHT_RUN, makeStairStepSync, pushWalkArrow, splitFlightSteps } from './stairCommon';
import { t } from '../../translation/markers';

// Flight width + step + step count sit ABOVE "distance to wall" (order 12),
// right after the shared width/depth rows (10-11) — same slots as stair.ts.
const PARAMS = {
  flightWidth: { kind: 'float', label: t('flight width'), default: 0.9, min: 0.5, max: 2.0, decimals: 2, distance: true, order: 11.3 },
  treadDepth: { kind: 'float', label: t('step'), default: 0.25, min: 0.15, max: 0.4, decimals: 2, distance: true, order: 11.4 },
  stepCount: { kind: 'int', label: t('number of steps'), default: 11, min: 2, order: 11.5 },
  reversed: { kind: 'boolean', label: t('reverse direction'), default: false },
  mirrored: { kind: 'boolean', label: t('mirror'), default: false },
} as const;

/** Effective flight width: the param clamped so both flights keep a run. */
function effectiveFlightWidth(width: number, depth: number, flightWidth: number): number {
  return Math.max(0.1, Math.min(flightWidth, width - MIN_FLIGHT_RUN, depth - MIN_FLIGHT_RUN));
}

/** Combined walkable run of both flights (m) — the landing adds no steps. */
function totalRun(width: number, depth: number, flightWidth: number): number {
  const fw = effectiveFlightWidth(width, depth, flightWidth);
  return Math.max(0.05, width - fw) + Math.max(0.05, depth - fw);
}

/**
 * L-shaped (quarter-turn) stair: the same hybrid LINE + FREE placement as the
 * straight stair — the drawn run is the footprint width (the lower flight's
 * side), the depth property is the upper flight's extent. The footprint is an
 * L: the lower flight walks local +X along the −Y edge, turns on a corner
 * landing at the far end and continues along +Y. Treads split the synced step
 * total over both flights by run share; `mirrored` flips the turn side (the
 * corner moves to the −X end) and `reversed` flips the walk direction.
 */
function buildStairLGlyph(frame: SketchObjectFrame, params: ParamValues<typeof PARAMS>): number[] {
  const out: number[] = [];
  const hw = frame.width / 2;
  const hd = frame.depth / 2;
  const su = params.mirrored ? -1 : 1;
  const fw = effectiveFlightWidth(frame.width, frame.depth, params.flightWidth);
  const runA = Math.max(0.05, frame.width - fw);
  const runB = Math.max(0.05, frame.depth - fw);

  pushPolyline(out, frame, [
    [-hw * su, -hd],
    [hw * su, -hd],
    [hw * su, hd],
    [(hw - fw) * su, hd],
    [(hw - fw) * su, -hd + fw],
    [-hw * su, -hd + fw],
    [-hw * su, -hd],
  ]);

  const { countA, countB } = splitFlightSteps(runA, runB, params.treadDepth);
  // Lower flight treads; k == countA is the landing boundary.
  for (let k = 1; k <= countA; k++) {
    const u = (-hw + (k * runA) / countA) * su;
    pushSegment(out, at(frame, u, -hd), at(frame, u, -hd + fw), frame.z);
  }
  // Upper flight treads; k == 0 is the landing boundary, the top edge is the
  // outline.
  for (let k = 0; k < countB; k++) {
    const n = -hd + fw + (k * runB) / countB;
    pushSegment(out, at(frame, (hw - fw) * su, n), at(frame, hw * su, n), frame.z);
  }

  // Walk line along both flight centerlines, cornering under the landing.
  const insetA = Math.min(0.2, 0.25 * runA);
  const insetB = Math.min(0.2, 0.25 * runB);
  const headSize = Math.min(0.15, fw * 0.3);
  pushWalkArrow(
    out,
    frame,
    [
      [(-hw + insetA) * su, -hd + fw / 2],
      [(hw - fw / 2) * su, -hd + fw / 2],
      [(hw - fw / 2) * su, hd - insetB],
    ],
    params.reversed,
    headSize,
  );

  return out;
}

/**
 * Handle layout: the run-end handles sit on the LOWER flight's centerline
 * (the stair's entry side), and a depth handle sits on the upper flight's
 * far end — dragging it stretches that flight, the lower edge staying
 * pinned. The depth handle follows the mirrored corner.
 */
function stairLResizeHandles(frame: SketchObjectFrame, params: ParamValues<typeof PARAMS>): SketchObjectResizeHandleLayout {
  const hw = frame.width / 2;
  const hd = frame.depth / 2;
  const su = params.mirrored ? -1 : 1;
  const fw = effectiveFlightWidth(frame.width, frame.depth, params.flightWidth);
  return {
    start: [-hw, -hd + fw / 2],
    end: [hw, -hd + fw / 2],
    depth: [{ u: (hw - fw / 2) * su, n: hd, side: 1 }],
  };
}

registerSketchObjectType(
  defineSketchObjectType({
    id: 'stair_l',
    label: t('L-shaped stair'),
    category: SketchObjectCategory.FIXTURES,
    anchor: SketchObjectAnchorKind.LINE,
    constraint: { kind: 'FREE' },
    defaultSize: { width: 2.5, depth: 2.0 },
    params: PARAMS,
    glyph: { kind: 'parametric', build: buildStairLGlyph },
    resizeHandles: stairLResizeHandles,
    syncParams: makeStairStepSync('stair_l', totalRun),
  }),
);
